import { useState, useEffect, type ReactNode } from "react";
import { BookOpen, Clock, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ReportJobStatus } from "@/types/models";
import type { Section } from "./SortableRow";

interface AIInstructionsCellProps {
  section: Section;
  onSelectPrompt: () => void;
  onGenerate: () => void;
  onSaveInstruction?: (sectionId: string, value: string) => void;
  sectionStatus?: ReportJobStatus;
  queuePosition?: number;
  queueSize?: number;
  /** The original library prompt text; when provided enables the reset link */
  originalPriming?: string | null;
  /** When true, the idle-state button reads "Regenerate" instead of "Generate" */
  hasContent?: boolean;
}

export const AIInstructionsCell = ({
  section,
  onSelectPrompt,
  onGenerate,
  onSaveInstruction,
  sectionStatus,
  queuePosition,
  queueSize,
  originalPriming,
  hasContent,
}: AIInstructionsCellProps) => {
  const [localValue, setLocalValue] = useState(section.priming ?? "");

  // Keep local state in sync when the section priming changes externally (e.g. after prompt selection)
  useEffect(() => {
    setLocalValue(section.priming ?? "");
  }, [section.priming]);

  const handleBlur = () => {
    const trimmed = localValue.trim();
    if (trimmed !== (section.priming ?? "").trim() && onSaveInstruction) {
      onSaveInstruction(section.id, trimmed);
    }
  };

  const handleReset = () => {
    const resetValue = originalPriming ?? "";
    setLocalValue(resetValue);
    if (onSaveInstruction) {
      onSaveInstruction(section.id, resetValue);
    }
  };

  // Show the reset link when originalPriming is available and the current value differs
  const showReset =
    originalPriming != null &&
    localValue.trim() !== (originalPriming ?? "").trim();

  const isDisabled = sectionStatus === "Processing" || (sectionStatus === "Queued" && (queueSize ?? 0) > 0);

  let buttonContent: ReactNode;
  if (sectionStatus === "Queued") {
    if (queueSize === 0) {
      buttonContent = <><Sparkles className="w-3 h-3 mr-1" />Generate</>;
    } else {
      const queueLabel = queuePosition && queueSize ? `Queued ${queuePosition} of ${queueSize}` : "Queued";
      buttonContent = <><Clock className="w-3 h-3 mr-1" />{queueLabel}</>;
    }
  } else if (sectionStatus === "Processing") {
    buttonContent = <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating…</>;
  } else {
    buttonContent = <><Sparkles className="w-3 h-3 mr-1" />{hasContent ? "Regenerate" : "Generate"}</>;
  }

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-start gap-1.5 w-full">
        {/* AI Instruction - editable */}
        <textarea
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          placeholder="Enter AI instructions…"
          rows={1}
          className="flex-1 min-h-[80px] max-h-[400px] text-sm p-3 bg-slate-50 border border-slate-200 rounded-md leading-relaxed overflow-y-auto focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
        />

        {/* Buttons */}
        <div className="flex flex-row items-center gap-1.5 shrink-0">
          <Button
            size="icon"
            variant="brand"
            onClick={onSelectPrompt}
            title="Select Prompt"
            className="h-7 w-7"
          >
            <BookOpen className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Reset to library prompt link */}
      {showReset && (
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-1 text-xs text-brand hover:text-brand/80 transition-colors self-start"
        >
          <RotateCcw className="w-3 h-3" />
          Reset to library prompt
        </button>
      )}

      <Button
        size="sm"
        variant="brand"
        onClick={onGenerate}
        disabled={isDisabled}
        className="h-7 text-xs px-2 mt-4"
      >
        {buttonContent}
      </Button>
    </div>
  );
};
